import { Controller } from "@hotwired/stimulus";

const MAX_RULES = 30;

export function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Marks a string as already-safe HTML so the `html` tag below passes it through
// unescaped — for the rare case of interpolating pre-built markup (e.g. a joined
// list of <option> tags) rather than a plain value.
class SafeHtml {
  constructor(value) {
    this.value = String(value);
  }
}
export function raw(value) {
  return new SafeHtml(value);
}

// Tagged template that escapes every interpolated value by default (StreamCommand-ojcs) —
// the opposite of escapeHtml(), which requires each call site to remember to opt in.
// Wrap a value in raw() to opt out when it's already-built HTML.
export function html(strings, ...values) {
  return strings.reduce((acc, str, i) => {
    if (i === 0) return str;
    const value = values[i - 1];
    const rendered = value instanceof SafeHtml ? value.value : escapeHtml(value);
    return acc + rendered + str;
  }, "");
}

export default class ReportThresholdsController extends Controller {
  static targets = ["list", "rule", "json"];
  static values = {
    reportsIn: String,
    minutes: String,
    remove: String,
    actionLabels: Object,
    paramPlaceholders: Object,
  };

  connect() {
    this.#serialize();
  }

  addRule() {
    if (this.ruleTargets.length >= MAX_RULES) return;

    const el = document.createElement("div");
    el.className = "flex flex-wrap items-center gap-2 mb-2 border border-base-300 rounded p-3";
    el.dataset.reportThresholdsTarget = "rule";
    el.innerHTML = this.#ruleHTML({});
    this.listTarget.appendChild(el);
    this.#serialize();
  }

  removeRule(event) {
    event.currentTarget.closest("[data-report-thresholds-target='rule']")?.remove();
    this.#serialize();
  }

  onChange(event) {
    const rule = event.currentTarget.closest("[data-report-thresholds-target='rule']");
    if (event.currentTarget.dataset.field === "action") {
      const action = event.currentTarget.value;
      const paramInput = rule.querySelector("[data-field='param']");
      if (paramInput) paramInput.placeholder = this.paramPlaceholdersValue[action] || "";
    }
    this.#serialize();
  }

  #readRuleFields(rule) {
    const f = (s) => rule.querySelector(`[data-field='${s}']`);
    return {
      count: parseInt(f("count")?.value || "0", 10),
      window_minutes: parseInt(f("window_minutes")?.value || "0", 10),
      action: f("action")?.value || "",
      param: (f("param")?.value || "").trim(),
    };
  }

  #buildRule(rule) {
    const { count, window_minutes, action, param } = this.#readRuleFields(rule);
    if (!count || !window_minutes || !action) return null;
    const obj = { count, window_minutes, action };
    if (action === "send_message") obj.channel_id = param;
    else if (action === "dm_user") obj.dm_text = param;
    else if (action === "toggle_role") obj.role_id = param;
    return obj;
  }

  #serialize() {
    const rules = this.ruleTargets.map((rule) => this.#buildRule(rule)).filter(Boolean);

    if (this.hasJsonTarget) {
      this.jsonTarget.value = JSON.stringify(rules);
    }
  }

  #paramValue(rule) {
    return rule.channel_id || rule.role_id || rule.dm_text || "";
  }

  #ruleHTML(rule) {
    const actions = ["open_case", "send_message", "dm_user", "toggle_role"];
    const selectedAction = rule.action || "open_case";
    const paramValue = this.#paramValue(rule);
    const labels = this.actionLabelsValue;
    const opts = actions
      .map(
        (a) =>
          html`<option value="${a}" ${raw(a === selectedAction ? " selected" : "")}>
            ${labels[a] || a}
          </option>`,
      )
      .join("");

    return html`
      <input
        type="number"
        class="input input-bordered input-xs w-16"
        min="1"
        placeholder="N"
        value="${rule.count || ""}"
        data-field="count"
        data-action="input->report-thresholds#onChange"
      />
      <span class="text-xs text-base-content/60">${this.reportsInValue || "reports in"}</span>
      <input
        type="number"
        class="input input-bordered input-xs w-20"
        min="1"
        placeholder="60"
        value="${rule.window_minutes || ""}"
        data-field="window_minutes"
        data-action="input->report-thresholds#onChange"
      />
      <span class="text-xs text-base-content/60">${this.minutesValue || "minutes →"}</span>
      <select
        class="select select-bordered select-xs"
        data-field="action"
        data-action="change->report-thresholds#onChange"
      >
        ${raw(opts)}
      </select>
      <input
        type="text"
        class="input input-bordered input-xs flex-1"
        placeholder="${this.paramPlaceholdersValue[selectedAction] || ""}"
        value="${paramValue}"
        data-field="param"
        data-action="input->report-thresholds#onChange"
      />
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error shrink-0"
        aria-label="${this.removeValue || "Remove threshold"}"
        data-action="click->report-thresholds#removeRule"
      >
        <i class="fa-solid fa-trash-can"></i>
      </button>
    `;
  }
}
